// Base union type - just the string literals
export type MetricValues =
  // Composition metrics
  | 'headcount'
  | 'growth_rate'
  | 'hiring_rate'
  | 'attrition_rate'
  | 'tenure'
  | 'salary'
  // Share of segment
  | 'share_of_headcount'
  // Transition metrics
  | 'transitions_inflow'
  | 'transitions_outflow'
  | 'transitions_inflows_and_outflows'
  // Posting metrics
  | 'posting_salary'
  | 'time_to_fill'
  | 'postings_active'
  | 'postings_new'
  | 'postings_removed'
  | 'postings_volume'
  // Pay metrics
  | 'total_compensation'
  | 'base_salary'
  | 'total_spend_salary'
  | 'total_spend_compensation'
  | 'contractor_pay_rate'
  // Pay percentile metrics
  | 'base_salary_percentiles'
  | 'total_compensation_percentiles'
  | 'contractor_pay_rate_percentiles'
  // Sentiment metrics
  | 'overall'
  | 'sentiment_top_bottom'
  | 'business_outlook'
  | 'career_growth'
  | 'compensation'
  | 'benefits'
  | 'healthcare'
  | 'retirement_pension'
  | 'childcare'
  | 'education_assistance'
  | 'wellness_programs'
  | 'work_life_balance'
  | 'diversity_and_inclusion'
  | 'leadership'
  | 'culture'
  | 'remote_work'
  | 'onboarding'
  | 'mentorship'
  | 'technology'
  | 'promotion_opportunities'
  | 'paid_time_off'
  | 'transparency'
  | 'efficiency_and_bureaucracy'
  | 'product_and_service_quality'
  | 'prestige'
  | 'coworkers'
  | 'mission_and_vision'
  | 'workplace_facilities'
  | 'training'
  | 'clients_and_customers'
  | 'company_growth'
  | 'schedule_flexibility'
  | 'job_security'
  | 'overtime'
  | 'family_support'
  | 'commission'
  | 'independence'
  | 'commute'
  | 'safety'
  | 'internal_communication'
  | 'office_politics'
  | 'harassment'
  | 'honesty'
  | 'ethics'
  | 'commitment'
  | 'compliance'
  | 'integrity'
  | 'positive_review'
  | 'negative_review'
  // Screener metrics
  | 'screener_headcount'
  | 'screener_growth'
  | 'screener_growth_yoy'
  | 'screener_hiring'
  | 'screener_attrition'
  | 'screener_salary'
  | 'screener_tenure'
  | 'screener_inflow'
  | 'screener_outflow'
  // Talent Discovery
  | 'td_users'
  // Market Conditions
  | 'entity_summary'
  | 'demand_supply'
  | 'demand_supply_percentile'
  | 'wage_pressure'
  | 'wage_pressure_percentile'
  | 'market_tightness'
  | 'market_share'
  | 'target_companies_rec'
  | 'time_to_fill_percentile'
  // Subsidiaries
  | 'subsidiary_count'
  // Map
  | 'overview_map';

export type MetricOption = {
  value: MetricValues;
  label: string;
};

export type GroupedMetricOption = {
  label: string;
  options: MetricOption[];
};

const transitionGroupedMetricOptions: GroupedMetricOption[] = [
  {
    label: 'Flow Type',
    options: [
      { value: 'transitions_inflow', label: 'Inflow' },
      { value: 'transitions_outflow', label: 'Outflow' },
      {
        value: 'transitions_inflows_and_outflows',
        label: 'Inflows & Outflows',
      },
    ],
  },
];

export const sentimentGroupedMetricOptions: GroupedMetricOption[] = [
  {
    label: 'Overview',
    options: [
      { value: 'sentiment_top_bottom', label: 'Sentiment Drivers' },
      { value: 'overall', label: 'Overall Sentiment' },
    ],
  },
  {
    label: 'Compensation & Benefits',
    options: [
      { value: 'compensation', label: 'Compensation Sentiment' },
      { value: 'benefits', label: 'Benefits Sentiment' },
      { value: 'healthcare', label: 'Healthcare Sentiment' },
      { value: 'retirement_pension', label: 'Retirement & Pension Sentiment' },
      { value: 'childcare', label: 'Childcare Sentiment' },
      {
        value: 'education_assistance',
        label: 'Education Assistance Sentiment',
      },
      { value: 'wellness_programs', label: 'Wellness Programs Sentiment' },
      { value: 'commission', label: 'Commission Sentiment' },
    ],
  },
  {
    label: 'Career Growth & Job Security',
    options: [
      { value: 'career_growth', label: 'Career Growth Sentiment' },
      { value: 'onboarding', label: 'Onboarding Sentiment' },
      { value: 'mentorship', label: 'Mentorship Sentiment' },
      {
        value: 'promotion_opportunities',
        label: 'Promotion Opportunities Sentiment',
      },
      { value: 'training', label: 'Training Sentiment' },
      { value: 'job_security', label: 'Job Security Sentiment' },
      { value: 'prestige', label: 'Prestige Sentiment' },
    ],
  },
  {
    label: 'Business & Outlook',
    options: [
      { value: 'business_outlook', label: 'Business Outlook Sentiment' },
      {
        value: 'product_and_service_quality',
        label: 'Product & Service Quality Sentiment',
      },
      { value: 'mission_and_vision', label: 'Mission & Vision Sentiment' },
      {
        value: 'clients_and_customers',
        label: 'Clients & Customers Sentiment',
      },
      { value: 'company_growth', label: 'Company Growth Sentiment' },
    ],
  },
  {
    label: 'Environment & Culture',
    options: [
      { value: 'culture', label: 'Culture Sentiment' },
      { value: 'coworkers', label: 'Coworkers Sentiment' },
      {
        value: 'workplace_facilities',
        label: 'Workplace Facilities Sentiment',
      },
      { value: 'safety', label: 'Safety Sentiment' },
      { value: 'office_politics', label: 'Office Politics Sentiment' },
      { value: 'harassment', label: 'Harassment Sentiment' },
      { value: 'leadership', label: 'Leadership Sentiment' },
      { value: 'independence', label: 'Independence Sentiment' },
    ],
  },
  {
    label: 'Ethics & Values',
    options: [
      {
        value: 'diversity_and_inclusion',
        label: 'Diversity & Inclusion Sentiment',
      },
      { value: 'honesty', label: 'Honesty Sentiment' },
      { value: 'ethics', label: 'Ethics Sentiment' },
      { value: 'commitment', label: 'Commitment Sentiment' },
      { value: 'integrity', label: 'Integrity Sentiment' },
    ],
  },
  {
    label: 'Flexibility & Logistics',
    options: [
      { value: 'work_life_balance', label: 'Work-Life Balance Sentiment' },
      { value: 'remote_work', label: 'Remote Work Sentiment' },
      { value: 'paid_time_off', label: 'Paid Time Off Sentiment' },
      {
        value: 'schedule_flexibility',
        label: 'Schedule Flexibility Sentiment',
      },
      { value: 'commute', label: 'Commute Sentiment' },
      { value: 'overtime', label: 'Overtime Sentiment' },
      { value: 'family_support', label: 'Family Support Sentiment' },
    ],
  },
  {
    label: 'Operations & Efficiency',
    options: [
      { value: 'technology', label: 'Technology Sentiment' },
      { value: 'transparency', label: 'Transparency Sentiment' },
      {
        value: 'efficiency_and_bureaucracy',
        label: 'Efficiency & Bureaucracy Sentiment',
      },
      {
        value: 'internal_communication',
        label: 'Internal Communication Sentiment',
      },
      { value: 'compliance', label: 'Compliance Sentiment' },
    ],
  },
];

const screenerMetricOptions: MetricOption[] = [
  { value: 'screener_headcount', label: 'Headcount' },
  { value: 'screener_growth', label: 'Growth' },
  { value: 'screener_growth_yoy', label: 'Growth YoY' },
  { value: 'screener_hiring', label: 'Hiring' },
  { value: 'screener_attrition', label: 'Attrition' },
  { value: 'screener_salary', label: 'Salary' },
  { value: 'screener_tenure', label: 'Tenure' },
  { value: 'screener_inflow', label: 'Inflow' },
  { value: 'screener_outflow', label: 'Outflow' },
];

export const groupedMetricOptions: GroupedMetricOption[] = [
  {
    label: 'Workforce Dynamics',
    options: [
      { value: 'headcount', label: 'Headcount' },
      { value: 'growth_rate', label: 'Growth Rate' },
      { value: 'hiring_rate', label: 'Hiring Rate' },
      { value: 'attrition_rate', label: 'Attrition Rate' },
      { value: 'tenure', label: 'Tenure' },
      { value: 'salary', label: 'Salary' },
    ],
  },
  {
    label: 'Screener',
    options: screenerMetricOptions,
  },
  {
    label: 'Postings',
    options: [
      { value: 'posting_salary', label: 'Posting Salary' },
      { value: 'time_to_fill', label: 'Time to Fill' },
      { value: 'time_to_fill_percentile', label: 'Time to Fill Percentile' },
      { value: 'postings_active', label: 'Active Postings' },
      { value: 'postings_new', label: 'New Postings' },
      { value: 'postings_removed', label: 'Removed Postings' },
      { value: 'postings_volume', label: 'Postings Volume' },
    ],
  },
  {
    label: 'Pay',
    options: [
      { value: 'base_salary', label: 'Base Salary' },
      { value: 'total_compensation', label: 'Total Compensation' },
      { value: 'contractor_pay_rate', label: 'Hourly Rate' },
      { value: 'base_salary_percentiles', label: 'Base Salary (Percentiles)' },
      {
        value: 'total_compensation_percentiles',
        label: 'Total Compensation (Percentiles)',
      },
      {
        value: 'contractor_pay_rate_percentiles',
        label: 'Hourly Rate (Percentiles)',
      },
      { value: 'total_spend_salary', label: 'Total Spend (Salary)' },
      {
        value: 'total_spend_compensation',
        label: 'Total Spend (Compensation)',
      },
    ],
  },

  {
    label: 'Transitions',
    options: transitionGroupedMetricOptions.flatMap((group) => group.options),
  },
  {
    label: 'Market Conditions',
    options: [
      { value: 'entity_summary', label: 'Summary' },
      { value: 'demand_supply', label: 'Demand/Supply' },
      { value: 'demand_supply_percentile', label: 'Demand/Supply Percentile' },
      { value: 'wage_pressure', label: 'Wage Pressure' },
      { value: 'wage_pressure_percentile', label: 'Wage Pressure Percentile' },
      { value: 'market_tightness', label: 'Market Tightness' },
      { value: 'market_share', label: 'Market Share' },
      { value: 'target_companies_rec', label: 'Target Companies' },
    ],
  },
  {
    label: 'Talent Discovery',
    options: [{ value: 'td_users', label: 'Employees' }],
  },
  {
    label: 'Sentiment',
    options: [
      ...sentimentGroupedMetricOptions.flatMap((group) => group.options),
      { value: 'positive_review', label: 'Positive Reviews' },
      { value: 'negative_review', label: 'Negative Reviews' },
    ],
  },
  {
    label: 'Map',
    options: [{ value: 'overview_map', label: 'Overview Map' }],
  },
];

/**
 * Flat `value → label` view of the grouped options above, DERIVED rather than
 * hand-listed so a metric can never carry one label in the picker and another
 * wherever this lookup is read.
 */
const METRIC_LABEL_BY_VALUE: ReadonlyMap<string, string> = new Map(
  groupedMetricOptions.flatMap((group) =>
    group.options.map((option): [string, string] => [
      option.value,
      option.label,
    ])
  )
);

/**
 * Display name for a metric — what a HUMAN should see wherever a metric is
 * named in prose.
 *
 * Exists because the AI assistant's chart digests carried only the internal
 * key, so its grounded narration echoed it straight at the user ("London's
 * dashboard shows attrition_rate at 11.4% … while sentiment_top_bottom is
 * strongest on Commute", reported on stage 2026-08-11). Model-facing summaries
 * name metrics through here so there is no internal identifier to copy.
 *
 * A key outside the catalog is humanized (`some_future_metric` → "Some Future
 * Metric") rather than passed through raw. A value that is already PROSE (it
 * contains whitespace — the assistant's synthetic digests put an intent
 * summary in the metric field) is returned untouched: title-casing a sentence
 * would be worse than leaving it alone.
 */
export function metricDisplayLabel(metric: string): string {
  const known = METRIC_LABEL_BY_VALUE.get(metric);
  if (known !== undefined) return known;
  if (/\s/.test(metric)) return metric;
  return metric
    .split('_')
    .filter((word) => word.length > 0)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');
}
